import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReflectionStore } from "../../../hooks/useReflectionStore";
import { useGrowStore } from "../../../hooks/useGrowStore";

/* ─── Constants ─── */
const ANALYZE_CLASS_IDS = [1, 2, 3];

const ACE_META = [
  {
    key: "A" as const,
    label: "Analyze",
    desc: "비판적 분석",
    detail: "AI 결과물의 오류·한계를 발견하는 능력",
    icon: "🔍",
    color: "#FF8C00",
    colorFade: "#FFF3E0",
    base: 30,
  },
  {
    key: "C" as const,
    label: "Create",
    desc: "생성 & 적용",
    detail: "목적에 맞는 프롬프트로 결과물을 만드는 능력",
    icon: "⚡",
    color: "#00D873",
    colorFade: "#E8F5E9",
    base: 65,
  },
  {
    key: "E" as const,
    label: "Evaluate",
    desc: "평가 & 수정",
    detail: "AI 초안을 인간의 시각으로 다듬는 능력",
    icon: "✏️",
    color: "#7C3AED",
    colorFade: "#EDE7F6",
    base: 20,
  },
];

const RECOMMENDATIONS: Record<string, { text: string; cta: string; route: string }> = {
  A: {
    text: "AI 결과물을 더 꼼꼼히 검증해보세요. 환각 현상 탐지 훈련이 필요해요.",
    cta: "Analyze 챌린지 시작",
    route: "/learn/1",
  },
  C: {
    text: "프롬프트 빌더로 더 많이 직접 만들어봐야 해요.",
    cta: "Create 챌린지 시작",
    route: "/learn/4",
  },
  E: {
    text: "AI 초안을 나만의 것으로 다듬는 연습이 가장 부족해요. 성찰 일지를 더 써보세요.",
    cta: "성찰 일지 작성",
    route: "/journal",
  },
};

/* ─── Radar Chart (SVG) ─── */
function ACERadarChart({
  a,
  c,
  e,
  animate,
}: {
  a: number;
  c: number;
  e: number;
  animate: boolean;
}) {
  const cx = 80, cy = 85, r = 60;
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const pt = (angle: number, pct: number) => ({
    x: cx + r * (pct / 100) * Math.cos(toRad(angle)),
    y: cy + r * (pct / 100) * Math.sin(toRad(angle)),
  });

  const grid = (level: number) => {
    const pa = pt(-90, level);
    const pc = pt(30, level);
    const pe = pt(150, level);
    return `${pa.x},${pa.y} ${pc.x},${pc.y} ${pe.x},${pe.y}`;
  };

  const curA = animate ? a : 0;
  const curC = animate ? c : 0;
  const curE = animate ? e : 0;

  const dA = pt(-90, curA);
  const dC = pt(30, curC);
  const dE = pt(150, curE);
  const polygon = `${dA.x},${dA.y} ${dC.x},${dC.y} ${dE.x},${dE.y}`;

  const axisA = pt(-90, 100);
  const axisC = pt(30, 100);
  const axisE = pt(150, 100);

  return (
    <svg width="160" height="170" viewBox="0 0 160 170" className="flex-shrink-0">
      {/* Grid */}
      {[25, 50, 75, 100].map((lvl, i) => (
        <polygon
          key={i}
          points={grid(lvl)}
          fill="none"
          stroke="#E8E9EB"
          strokeWidth={lvl === 100 ? 1.5 : 1}
          strokeDasharray={lvl < 100 ? "3,3" : "0"}
        />
      ))}

      {/* Axes */}
      {[axisA, axisC, axisE].map((ax, i) => (
        <line key={i} x1={cx} y1={cy} x2={ax.x} y2={ax.y} stroke="#E8E9EB" strokeWidth="1.5" />
      ))}

      {/* Fill polygon */}
      <polygon
        points={polygon}
        fill="rgba(0,216,115,0.12)"
        stroke="#00D873"
        strokeWidth="2"
        strokeLinejoin="round"
        style={{ transition: "all 0.8s ease" }}
      />

      {/* Data points */}
      <circle cx={dA.x} cy={dA.y} r="5" fill="#FF8C00" style={{ transition: "all 0.8s ease" }} />
      <circle cx={dC.x} cy={dC.y} r="5" fill="#00D873" style={{ transition: "all 0.8s ease" }} />
      <circle cx={dE.x} cy={dE.y} r="5" fill="#7C3AED" style={{ transition: "all 0.8s ease" }} />

      {/* Labels */}
      <text x={axisA.x} y={axisA.y - 10} textAnchor="middle" fontSize="12" fontWeight="800" fill="#FF8C00">A</text>
      <text x={axisA.x} y={axisA.y - 0} textAnchor="middle" fontSize="9" fill="#8B95A1">{a}%</text>
      <text x={axisC.x + 14} y={axisC.y + 4} textAnchor="middle" fontSize="12" fontWeight="800" fill="#00D873">C</text>
      <text x={axisC.x + 14} y={axisC.y + 14} textAnchor="middle" fontSize="9" fill="#8B95A1">{c}%</text>
      <text x={axisE.x - 14} y={axisE.y + 4} textAnchor="middle" fontSize="12" fontWeight="800" fill="#7C3AED">E</text>
      <text x={axisE.x - 14} y={axisE.y + 14} textAnchor="middle" fontSize="9" fill="#8B95A1">{e}%</text>
    </svg>
  );
}

/* ─── Bar Row ─── */
function ACEBar({
  meta,
  value,
  isWeakest,
  animate,
}: {
  meta: (typeof ACE_META)[number];
  value: number;
  isWeakest: boolean;
  animate: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-2xl transition-all ${
        isWeakest ? "ring-2" : ""
      }`}
      style={isWeakest ? { ringColor: meta.color, backgroundColor: meta.colorFade } : {}}
    >
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-base font-extrabold"
        style={{ backgroundColor: meta.colorFade, color: meta.color }}
      >
        {meta.key}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-extrabold text-[#191F28]">{meta.label}</span>
            <span className="text-[10px] text-[#8B95A1]">{meta.desc}</span>
            {isWeakest && (
              <span
                className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: meta.colorFade, color: meta.color }}
              >
                약점
              </span>
            )}
          </div>
          <span className="text-xs font-extrabold flex-shrink-0" style={{ color: meta.color }}>
            {value}%
          </span>
        </div>
        <div className="h-2 bg-[#F2F4F6] rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-1000 ease-out"
            style={{
              width: animate ? `${value}%` : "0%",
              backgroundColor: meta.color,
            }}
          />
        </div>
      </div>
    </div>
  );
}

/* ─── Main ─── */
export default function ACEBalanceSection() {
  const { reflections } = useReflectionStore();
  const { store } = useGrowStore();
  const navigate = useNavigate();
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAnimate(true), 200);
    return () => clearTimeout(t);
  }, []);

  // Compute ACE scores from real data + base
  const analyzeCompleted = store.completedClasses.filter((c) =>
    ANALYZE_CLASS_IDS.includes(c.classId)
  ).length;
  const createCompleted = store.completedClasses.filter(
    (c) => !ANALYZE_CLASS_IDS.includes(c.classId)
  ).length;

  const aReal = analyzeCompleted * 20 + reflections.filter((r) => r.q1.length > 15).length * 8;
  const cReal = createCompleted * 20 + reflections.filter((r) => r.q2.length > 15).length * 8;
  const eReal = reflections.filter((r) => r.q3.length > 15).length * 12;

  const baseA = ACE_META[0].base;
  const baseC = ACE_META[1].base;
  const baseE = ACE_META[2].base;

  const rawA = Math.min(baseA + aReal, 100);
  const rawC = Math.min(baseC + cReal, 100);
  const rawE = Math.min(baseE + eReal, 100);

  const scores = { A: rawA, C: rawC, E: rawE };

  const weakest = ACE_META.reduce((min, item) =>
    scores[item.key] < scores[min.key] ? item : min
  );

  const rec = RECOMMENDATIONS[weakest.key];

  return (
    <div className="bg-white rounded-3xl p-7">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-radar-line text-[#00D873] text-lg" />
            </div>
            <span className="text-xs font-bold text-[#00D873] tracking-widest uppercase">ACE 프레임워크</span>
          </div>
          <h3 className="text-xl font-black text-[#191F28]">나의 ACE 균형</h3>
          <p className="text-xs text-[#8B95A1] mt-0.5">세 역량이 균형 잡힐수록 진짜 AI 인재예요</p>
        </div>
        <span className="text-[10px] text-[#8B95A1] font-medium bg-[#F2F4F6] px-2.5 py-1 rounded-full whitespace-nowrap">
          {store.completedClasses.length + reflections.length}개 활동 기반
        </span>
      </div>

      {/* Main: Radar + Bars */}
      <div className="flex flex-col lg:flex-row gap-6 mb-6">
        {/* Radar */}
        <div className="flex items-center justify-center lg:justify-start">
          <ACERadarChart a={rawA} c={rawC} e={rawE} animate={animate} />
        </div>

        {/* Bars */}
        <div className="flex-1 space-y-2">
          {ACE_META.map((meta) => (
            <ACEBar
              key={meta.key}
              meta={meta}
              value={scores[meta.key]}
              isWeakest={meta.key === weakest.key}
              animate={animate}
            />
          ))}
        </div>
      </div>

      {/* Detail cards */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        {ACE_META.map((meta) => (
          <div
            key={meta.key}
            className="rounded-xl p-3 text-center"
            style={{ backgroundColor: meta.colorFade }}
          >
            <div className="text-lg mb-1">{meta.icon}</div>
            <p className="text-[10px] font-bold" style={{ color: meta.color }}>{meta.key} — {meta.label}</p>
            <p className="text-[9px] text-[#8B95A1] mt-0.5 leading-tight">{meta.detail}</p>
          </div>
        ))}
      </div>

      {/* Recommendation */}
      <div
        className="rounded-2xl p-4 flex items-start gap-3"
        style={{ backgroundColor: weakest.colorFade }}
      >
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0"
          style={{ backgroundColor: weakest.color + "22" }}
        >
          {weakest.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <span
              className="text-[10px] font-extrabold px-2 py-0.5 rounded-full text-white"
              style={{ backgroundColor: weakest.color }}
            >
              {weakest.key} 약점 감지
            </span>
            <span className="text-[10px] text-[#8B95A1]">{weakest.label} 역량 강화 필요</span>
          </div>
          <p className="text-xs text-[#3D4756] leading-relaxed">{rec.text}</p>
        </div>
        <button
          onClick={() => navigate(rec.route)}
          className="flex-shrink-0 px-3 py-2 text-[11px] font-extrabold text-white rounded-xl cursor-pointer hover:opacity-90 transition whitespace-nowrap"
          style={{ backgroundColor: weakest.color }}
        >
          {rec.cta} →
        </button>
      </div>
    </div>
  );
}
